// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { SparseCustomer } from '@/sparse/api/types';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { omit } from 'lodash-es';
import { ForwardedRef, HTMLAttributes, forwardRef } from 'react';

/**
 * Special ID used to identify the "create new customer" option in the dropdown.
 * This option appears when the user's input doesn't match any existing customers.
 */
export const CREATE_CUSTOMER_OPTION_ID = 'create-company';

/**
 * Determines if the option is equal to the value.
 *
 * @param option - A company option to compare.
 * @param value - A company value to compare.
 */
function isOptionEqualToValue(option: SparseCustomer, value: SparseCustomer) {
  return option.id === value.id;
}

/**
 * Returns a group label for a company option.
 *
 * @param option - A company option to get the group label from.
 */
function getGroupLabel(option: SparseCustomer) {
  return option.isManufacturer ? 'Manufacturer' : 'Companies';
}

/**
 * Renders a single company option.
 * Handles both regular company entries and the special "create customer" option.
 * For companies, displays role chips indicating customer and/or supplier status.
 *
 * @param props - The HTML attributes to apply to the list item.
 * @param option - A company option to render.
 */
function renderOption(
  props: HTMLAttributes<HTMLLIElement>,
  option: SparseCustomer
) {
  // Ensure that 'key' is not spread onto the list item element
  const attributes = omit(props, 'key');

  // Special rendering for the create new customer option
  if (option.id === CREATE_CUSTOMER_OPTION_ID) {
    return (
      <li {...attributes} key={option.id}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AddRounded fontSize="small" color="primary" />
          <Typography color="primary.main">
            Add company <b>{option.name}</b>
          </Typography>
        </Stack>
      </li>
    );
  }

  // Regular company option with archived state handling and role chips
  const color = option.isArchived ? 'text.secondary' : 'text.primary';

  return (
    <li {...attributes} key={option.id}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        width="100%"
        justifyContent="space-between"
      >
        <Typography color={color}>
          {option.name} {option.isArchived && '(Archived)'}
        </Typography>
        {!option.isManufacturer && (
          <Stack direction="row" spacing={0.5}>
            <Chip
              label="Customer"
              size="small"
              color="secondary"
              variant={option.isCustomer ? 'filled' : 'outlined'}
            />
            <Chip
              label="Supplier"
              size="small"
              color="secondary"
              variant={option.isSupplier ? 'filled' : 'outlined'}
            />
          </Stack>
        )}
      </Stack>
    </li>
  );
}

/**
 * Returns the label of the option.
 *
 * @param option - A company option to get the label from.
 */
function getOptionLabel(option: SparseCustomer) {
  return option.name;
}

interface Props<Option extends SparseCustomer>
  extends Omit<
    AutocompleteProps<Option, false, boolean, false>,
    | 'renderInput'
    | 'isOptionEqualToValue'
    | 'getOptionLabel'
    | 'groupBy'
    | 'renderOption'
  > {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * If provided, the placeholder text will be displayed in the input element.
   */
  placeholder?: string;
}

/**
 * Renders a company autocomplete component and forwards a ref to the input
 * element.
 */
function CompanyAutocompleteRenderer<Option extends SparseCustomer>(
  props: Props<Option>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { name, label, placeholder, error, helperText, ...autocompleteProps } =
    props;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...parameters}
      />
    );
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      groupBy={getGroupLabel}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
}

/**
 * An autocomplete component for selecting a company from a list of options.
 */
export const CompanyAutocomplete = forwardRef(CompanyAutocompleteRenderer);
